import {
    ISystemResponse,
    ITreeItemResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import { DataSourceQuery } from 'custom_hooks/data_hooks/data_source/data_source';
import { useSystems } from 'custom_hooks/data_hooks/system/useSystems';
import { useEffect, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { uniqueBy } from 'utils/array';
import { drainQueryCallback, Filter, toDataSourceResult } from 'utils/query_v3';
import { findMany, IKendoTreeViewItem, Tree, TreeItem, TreeItemType } from 'utils/tree';

export const isAssetOrAbove = (type: TreeItemType) =>
    type === 'Group' || type === 'Asset' || type === 'Assembly' || type === 'System';

const canContainAsset = (type: TreeItemType) =>
    type === 'Assembly' || type === 'Group' || type === 'System';

function isTreeItemResponse(
    response: ITreeItemResponse | ISystemResponse
): response is ITreeItemResponse {
    return 'TreeItemType' in response;
}

type FilterMode = 'assetAndAbove' | 'all';

export const getTreeItemFromResponse = (mode: FilterMode) => (
    item: ITreeItemResponse | ISystemResponse
): IKendoTreeViewItem<TreeItem> => ({
    text: item.Name,
    expanded: false,
    hasChildren: isTreeItemResponse(item)
        ? (mode === 'all' || canContainAsset(item.TreeItemType)) && item.HasChildren
        : item.Children.length > 0,
    items: [],
    model: {
        id: item.Id,
        name: item.Name,
        depth: -999,
        type: isTreeItemResponse(item) ? item.TreeItemType : 'System',
        hasAlarms: false,
        hasWarnings: false,
    },
});

const treeItemQueryForItem = (
    itemId: string
): DataSourceQuery<ITreeItemResponse, unknown> => ({ page, pageLimit }) =>
    toDataSourceResult(
        sdClient.getTreeItemChildren({
            treeItemId: itemId,
            page,
            pageLimit,
        })
    );

/**
 * Fetches and builds a tree structure containing all tree groups under
 * a particular location, down to the asset level. Groups are loaded as
 * they are expanded and updated immutably.
 */
export function useAssetTree({ locationId }: { locationId?: string }) {
    return useGroupTree({
        locationId,
        mode: 'assetAndAbove',
    });
}

export function useGroupTree({
    locationId,
    mode,
}: {
    locationId?: string;
    mode: FilterMode;
}) {
    const [tree, setTree] = useImmer<Tree<TreeItem>>([]);

    const treeItemMapper = useMemo(() => getTreeItemFromResponse(mode), [mode]);

    const { data } = useSystems({
        filter: locationId ? Filter.and(Filter.eq('LocationId', locationId)) : undefined,
        pageLimit: 1000,
    });

    useEffect(() => {
        if (data) {
            setTree(data.Result.map(treeItemMapper));
        }
    }, [data, setTree, treeItemMapper]);

    const handleTreeItemResponse = (parentId: string, res: ITreeItemResponse[]) => {
        setTree((draftTree) => {
            // this _should_ actually be a single item
            findMany(draftTree, parentId).forEach((item) => {
                if (item.expanded) {
                    const newItems = res.map(treeItemMapper);
                    const filteredNewItems =
                        mode === 'assetAndAbove'
                            ? newItems.filter(({ model }) => isAssetOrAbove(model.type))
                            : newItems;
                    // uniqueBy becasue these queries currently get rerun for
                    // pages we've already loaded if you open/close an item in
                    // the tree
                    item.items = uniqueBy(
                        [...item.items, ...filteredNewItems],
                        (el) => el.model.id
                    );
                }
            });
        });
    };

    const toggleItemExpand = (id: string) => {
        setTree((draftTree) => {
            drainQueryCallback(
                treeItemQueryForItem(id),
                {
                    filter: undefined,
                    pageLimit: 50,
                },
                (res) => handleTreeItemResponse(id, res.results)
            );

            findMany(draftTree, id).forEach((item) => {
                item.expanded = !item.expanded;
            });
        });
    };

    return { data: tree, toggleExpanded: toggleItemExpand };
}
